package com.dotdrives.web.pages.forms

import com.dotdrives.model.FormConfig
import com.dotdrives.model.FormField
import com.dotdrives.model.displayLabel
import com.dotdrives.web.pages.giving.givingContactFields
import react.FC
import react.Key
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.code
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

private val receiptKeys = setOf("name", "full_name", "first_name", "last_name", "email")
internal fun stableNewFieldKey(base: String, existing: Collection<String>): String {
    val stem = base.lowercase().replace(Regex("[^a-z0-9_]+"), "_").trim('_').take(36).ifBlank { "field" }
        .let { if (it.first().isLetter()) it else "field_$it" }
    var key = stem
    var suffix = 2
    while (key in existing) { key = "${stem}_${suffix++}" }
    return key
}

private val answerTypes = listOf("text" to "Short text", "textarea" to "Long text", "email" to "Email", "tel" to "Phone", "number" to "Number", "checkbox" to "Checkbox", "select" to "Choose one")

val FormDonorFieldsEditor = FC<FormTabProps> { props ->
    val state = props.state
    val controller = props.controller
    val (expanded, setExpanded) = useState<String?>(null)
    val ordered = state.draftFields.sortedBy(FormField::order)
    val fields = givingContactFields(FormConfig(fields = ordered))
    fun change(next: FormField) {
        if (!props.canWrite) return
        val rows = if (ordered.any { it.key == next.key }) ordered.map { if (it.key == next.key) next else it } else ordered + next
        controller.setFields(rows)
    }
    fun move(key: String, delta: Int) {
        if (!props.canWrite) return
        val from = ordered.indexOfFirst { it.key == key }
        val candidates = ordered.indices.filter { ordered[it].key !in receiptKeys }
        val position = candidates.indexOf(from)
        val to = candidates.getOrNull(position + delta) ?: return
        val rows = ordered.toMutableList()
        rows.add(to, rows.removeAt(from))
        controller.setFields(rows.mapIndexed { index, field -> field.copy(order = index) })
    }
    div {
        className = ClassName("forms-v2-contact-note")
        span { ariaHidden = true; +"🔒" }
        span { +"Name and email are collected for the receipt. Add, reorder or remove other donor details below." }
    }
    div {
        className = ClassName("forms-v2-field-list")
        fields.forEach { field ->
            val locked = field.key in receiptKeys
            val open = expanded == field.key
            val movable = ordered.filter { it.key !in receiptKeys }
            val position = movable.indexOfFirst { it.key == field.key }
            div {
                key = field.key.unsafeCast<Key>()
                className = ClassName("forms-v2-donor-field" + (if (open) " forms-v2-donor-field-open" else "") + (if (locked) " forms-v2-donor-field-locked" else ""))
                div {
                    className = ClassName("forms-v2-donor-field-heading")
                    if (locked) span { className = ClassName("forms-v2-field-lock"); ariaHidden = true; +"🔒" }
                    else div {
                        className = ClassName("forms-v2-reorder")
                        listOf(-1 to "↑", 1 to "↓").forEach { (delta, arrow) ->
                            button {
                                type = ButtonType.buttonType; ariaLabel = "Move ${field.displayLabel} ${if (delta < 0) "up" else "down"}"
                                disabled = !props.canWrite || position + delta !in movable.indices
                                onClick = { move(field.key, delta) }; +arrow
                            }
                        }
                    }
                    button {
                        type = ButtonType.buttonType; className = ClassName("forms-v2-donor-field-title")
                        ariaExpanded = open; ariaControls = "form-field-${field.key}".unsafeCast<web.dom.ElementId>(); ariaLabel = "Edit ${field.displayLabel} field"
                        onClick = { setExpanded(if (open) null else field.key) }
                        span { +field.displayLabel }
                        span { className = ClassName("forms-v2-type-pill"); +(answerTypes.firstOrNull { it.first == field.type }?.second ?: field.type) }
                    }
                    if (locked) span { className = ClassName("forms-v2-required-note"); +if (field.required) "Always required" else "Receipt field" }
                    else div {
                        className = ClassName("forms-v2-field-required")
                        span { +"Required" }
                        SwitchButton { on = field.required; enabled = props.canWrite; label = "${field.displayLabel} required"; onToggle = { change(field.copy(required = it)) } }
                    }
                }
                if (open) div {
                    id = "form-field-${field.key}".unsafeCast<web.dom.ElementId>(); className = ClassName("forms-v2-donor-field-body")
                    div {
                        className = ClassName("forms-v2-two-columns")
                        label {
                            className = ClassName("forms-v2-field")
                            span { +"Label shown to donors" }
                            input {
                                type = InputType.text; value = field.label ?: field.displayLabel
                                ariaLabel = "${field.key} donor label"; maxLength = 120; disabled = !props.canWrite
                                onChange = { change(field.copy(label = it.currentTarget.value)) }
                            }
                        }
                        label {
                            className = ClassName("forms-v2-field")
                            span { +"Answer type" }
                            select {
                                value = field.type; ariaLabel = "${field.key} answer type"; disabled = !props.canWrite || locked
                                onChange = {
                                    if (props.canWrite && !locked) change(field.copy(type = it.currentTarget.value, options = if (it.currentTarget.value == "select") emptyList() else null))
                                }
                                answerTypes.forEach { (type, name) -> option { value = type; +name } }
                            }
                        }
                    }
                    if (field.type == "select") label {
                        className = ClassName("forms-v2-field")
                        span { +"Options, one per line" }
                        textarea {
                            value = field.options.orEmpty().joinToString("\n"); rows = 3
                            ariaLabel = "${field.key} options"; disabled = !props.canWrite || locked
                            onChange = { if (props.canWrite && !locked) change(field.copy(options = it.currentTarget.value.split('\n'))) }
                        }
                    }
                    div {
                        className = ClassName("forms-v2-field-footer")
                        code { +"Export column: ${field.key}" }
                        if (!locked) button {
                            type = ButtonType.buttonType; className = ClassName("forms-v2-danger-link"); disabled = !props.canWrite
                            onClick = { if (props.canWrite) { controller.setFields(ordered.filterNot { it.key == field.key }); setExpanded(null) } }; +"Remove field"
                        }
                    }
                }
            }
        }
    }
    div {
        className = ClassName("forms-v2-quick-add")
        span { +"Add:" }
        listOf(Triple("Phone", "phone", "tel"), Triple("Address", "address", "text"), Triple("Comment", "comment", "textarea"), Triple("Dropdown", "choose_one", "select"), Triple("Checkbox", "agreement", "checkbox"), Triple("Custom…", "custom_field", "text")).forEach { (name, base, type) ->
            button {
                this.type = ButtonType.buttonType; className = ClassName("forms-v2-small-button")
                disabled = !props.canWrite || fields.size >= 30
                onClick = {
                    if (props.canWrite && fields.size < 30) {
                        val key = stableNewFieldKey(base, fields.map(FormField::key))
                        val label = when (type) { "select" -> "Choose one"; "checkbox" -> "I agree"; else -> name.removeSuffix("…") }
                        controller.setFields(ordered + FormField(key, type, false, (ordered.maxOfOrNull(FormField::order) ?: -1) + 1, if (type == "select") emptyList() else null, label = label))
                        setExpanded(key)
                    }
                }
                +name
            }
        }
    }
    div { className = ClassName("forms-v2-help"); +"Labels can change. Export columns stay the same so existing answers remain connected." }
}
